import { useState, useEffect, useCallback, useMemo } from 'react';
import { Flex, Heading, GridAlpha, Button, TextInput, useToasts } from '@applyboard/crystal-ui';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { StudentSchoolInformation } from '@ab/ams-school-api/src/services/studentsService/v1/studentsSerivce/types';
import { usePatchStudent } from '../../../../../data/usePatchStudent';
import { useSchoolCustomizations, StudentCustomizationField } from '~/data/useSchoolCustomizations';
import { validateRelevantStudentInformation, RelevantStudentInformationData } from './validation';

const PROVINCIAL_FIELD_LABELS: Record<string, string> = {
  ontarioEducationNumber: 'OEN',
  albertaStudentNumber: 'ASN',
  personalEducationNumber: 'PEN',
};

type ProvincialFieldKey = 'ontarioEducationNumber' | 'albertaStudentNumber' | 'personalEducationNumber';

const PROVINCIAL_FIELDS = new Set<string>(Object.keys(PROVINCIAL_FIELD_LABELS));

function getProvincialFields(
  customizationFields?: StudentCustomizationField[],
  schoolInformation?: StudentSchoolInformation
): ProvincialFieldKey[] {
  if (customizationFields) {
    return customizationFields.filter((f) => PROVINCIAL_FIELDS.has(f.type)).map((f) => f.type as ProvincialFieldKey);
  }

  if (!schoolInformation) return [];

  return (Object.keys(schoolInformation) as Array<keyof StudentSchoolInformation>).filter(
    (key): key is ProvincialFieldKey => PROVINCIAL_FIELDS.has(key)
  );
}

function buildFormData(
  provincialFields: ProvincialFieldKey[],
  schoolInformation?: StudentSchoolInformation
): RelevantStudentInformationData {
  const data: RelevantStudentInformationData = {
    schoolAssignedStudentId: String(schoolInformation?.schoolAssignedStudentId ?? ''),
  };
  for (const field of provincialFields) {
    data[field] = String(schoolInformation?.[field] ?? '');
  }
  return data;
}

type RelevantStudentInformationProps = Readonly<{
  schoolInformation?: StudentSchoolInformation;
  studentId?: string;
  applicationId: string;
}>;

export function RelevantStudentInformation({
  schoolInformation,
  studentId,
  applicationId,
}: RelevantStudentInformationProps) {
  const { isPatchingStudent, patchStudent } = usePatchStudent();
  const { schoolCustomizations } = useSchoolCustomizations();
  const toast = useToasts();

  const customizationSchoolInfo = schoolCustomizations?.studentCustomization?.schoolInformation;

  const provincialFields = useMemo(
    () => getProvincialFields(customizationSchoolInfo, schoolInformation),
    [customizationSchoolInfo, schoolInformation]
  );

  const allFieldCount = 1 + provincialFields.length;
  const responsiveColumns = { xs: '1fr', sm: 'repeat(3, 1fr)' };
  const responsiveRows = {
    xs: `repeat(${allFieldCount}, auto)`,
    sm: `repeat(${Math.ceil(allFieldCount / 3)}, auto)`,
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<RelevantStudentInformationData>(() =>
    buildFormData(provincialFields, schoolInformation)
  );

  useEffect(() => {
    if (!isEditing) {
      setEditedData(buildFormData(provincialFields, schoolInformation));
    }
  }, [schoolInformation, isEditing, provincialFields]);

  const handleFieldChange = useCallback((field: keyof RelevantStudentInformationData, value: string) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleCancel = useCallback(() => {
    setEditedData(buildFormData(provincialFields, schoolInformation));
    setIsEditing(false);
  }, [schoolInformation, provincialFields]);

  const handleSave = useCallback(async () => {
    const validation = validateRelevantStudentInformation(editedData);
    if (!validation.isValid && validation.errors) {
      validation.errors.forEach((error) => {
        toast.negative(new Error(error));
      });
      return;
    }

    if (!studentId) {
      toast.negative(new Error('Student ID is not available'));
      return;
    }

    const payload: Record<string, string | number | null> = {};
    let hasChanges = false;

    const studentIdValue = editedData.schoolAssignedStudentId ?? '';
    const originalStudentIdValue = String(schoolInformation?.schoolAssignedStudentId ?? '');
    if (studentIdValue !== originalStudentIdValue) {
      hasChanges = true;
      payload.schoolAssignedStudentId = studentIdValue !== '' ? studentIdValue : null;
    }

    for (const field of provincialFields) {
      const newValue = editedData[field] ?? '';
      const originalValue = String(schoolInformation?.[field] ?? '');
      if (newValue !== originalValue) {
        hasChanges = true;
        payload[field] = newValue !== '' ? Number(newValue) : null;
      }
    }

    if (!hasChanges) {
      setIsEditing(false);
      return;
    }

    try {
      await patchStudent({
        id: studentId,
        applicationId,
        payload: { schoolInformation: payload },
      });
      toast.positive('Student information updated successfully');
      setIsEditing(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update student information';
      toast.negative(new Error(errorMessage));
    }
  }, [editedData, studentId, applicationId, provincialFields, schoolInformation, patchStudent, toast]);

  return (
    <Flex justify="start" direction="column" gap={10}>
      <Flex justify="between" align="center">
        <Heading level={4}>Relevant student information</Heading>
        {!isEditing && studentId && (
          <Button size="sm" variant="outlined" onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        )}
      </Flex>
      {isEditing ? (
        <Flex direction="column" gap={6}>
          <GridAlpha rows={responsiveRows} columnGap={2} rowGap={6} columns={responsiveColumns}>
            <GridAlpha.Item>
              <TextInput
                label="STUDENT ID"
                value={editedData.schoolAssignedStudentId ?? ''}
                onChange={(value: string) => handleFieldChange('schoolAssignedStudentId', value)}
              />
            </GridAlpha.Item>
            {provincialFields.map((field) => (
              <GridAlpha.Item key={field}>
                <TextInput
                  label={PROVINCIAL_FIELD_LABELS[field]}
                  value={editedData[field] ?? ''}
                  onChange={(value: string) => handleFieldChange(field, value)}
                />
              </GridAlpha.Item>
            ))}
          </GridAlpha>
          <Flex gap={4} justify="end">
            <Button size="sm" variant="outlined" onClick={handleCancel} disabled={isPatchingStudent}>
              Cancel
            </Button>
            <Button size="sm" intent="primary" onClick={handleSave} loading={isPatchingStudent}>
              Save
            </Button>
          </Flex>
        </Flex>
      ) : (
        <GridAlpha rows={responsiveRows} columnGap={2} rowGap={6} columns={responsiveColumns}>
          <GridAlpha.Item>
            <LabelValue label="STUDENT ID" value={schoolInformation?.schoolAssignedStudentId ?? '--'} />
          </GridAlpha.Item>
          {provincialFields.map((field) => (
            <GridAlpha.Item key={field}>
              <LabelValue
                label={PROVINCIAL_FIELD_LABELS[field]}
                value={schoolInformation?.[field] != null ? String(schoolInformation[field]) : '--'}
              />
            </GridAlpha.Item>
          ))}
        </GridAlpha>
      )}
    </Flex>
  );
}
